import { Delete } from '@mui/icons-material';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { getCompanyConfig } from 'lib/api/company';
import { getDocumentsWithArrearCalculation } from 'lib/api/document';
import { activateNullFactoringRequest } from 'lib/api/factoring';
import { MoneyInput, SelectInput, SelectOption } from 'lib/common/Inputs';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import PageHeader, { ActionButton } from 'lib/designSystem/PageHeader';
import {
  calculateRequestDetails,
  formatMoney,
  normalizeDate,
} from 'lib/functions/utils';
import { PayoutDistribution } from 'lib/types/models';
import ApplicationsInOperationWarnings, {
  ComparisonRow,
} from 'pages/Finance/Components/Financing/common/applicationsInOperationWarnings';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

type PaymentGrouped = {
  amount: number;
  bankAccountDestinationId: string;
  bankAccountOriginId: string;
  cut: string;
};

const EMPTY_PAYMENT_GROUPED: PaymentGrouped = {
  bankAccountDestinationId: '',
  bankAccountOriginId: '',
  amount: 0,
  cut: '',
};

const AVAILABLE_CUTS: SelectOption<string>[] = [
  { value: '7000000', label: '$7.000.000' },
  { value: '5000000', label: '$5.000.000' },
  { value: 'sin-corte', label: 'Sin corte' },
];

const resolveCut = (cut: string, amount: number): number =>
  cut === 'sin-corte' ? amount : Number(cut);

const GenerateNominee = (props: any) => {
  const {
    selectedRequest,
    setUp,
    availableAccounts,
    originAccounts,
    setPayments,
    amount,
    handleExit,
    setSuccessOpen,
    payoutPlan,
    onSinNomina,
  } = props;
  const dispatch = useDispatch();
  const { operationToDepost } = amount
    ? { operationToDepost: amount }
    : calculateRequestDetails(selectedRequest, selectedRequest.expensesIVA);

  const [paymentGrouped, setPaymentGrouped] = useState<PaymentGrouped[]>([]);
  const [loading, setLoading] = useState(false);
  const canReloadAccounts = typeof setUp === 'function';

  const documentsAppliedIds =
    selectedRequest?.documentsToApply?.map((d: { id: string }) => d.id) || [];
  const operationDate = new Date(selectedRequest?.requestDate);

  const {
    data: documentsAppliedWithArrearCalculation,
    isLoading: isLoadingDocumentsAppliedWithArrearCalculation,
  } = getDocumentsWithArrearCalculation(
    dispatch,
    documentsAppliedIds,
    operationDate,
  );
  const {
    data: allowOperationIfFutureConciliationsValue,
    isLoading: isLoadingAllowOperationIfFutureConciliations,
  } = getCompanyConfig(dispatch, 'allow_operation_if_future_conciliations');
  const allowOperationIfFutureConciliations =
    allowOperationIfFutureConciliationsValue === '1';

  const isLoading =
    isLoadingDocumentsAppliedWithArrearCalculation ||
    isLoadingAllowOperationIfFutureConciliations;

  const documentsToApply = selectedRequest?.documentsToApply ?? [];
  const comparisonRows: ComparisonRow[] =
    documentsAppliedWithArrearCalculation?.documents
      ? documentsToApply.map(
          (doc: { id: string; amountToConciliate?: number }) => {
            const withArrear =
              documentsAppliedWithArrearCalculation.documents.find(
                (d) => d.id === doc.id,
              );
            const lastConciliation =
              withArrear?.conciliationLog?.length &&
              withArrear?.conciliationLog?.length > 0
                ? withArrear.conciliationLog[
                    withArrear.conciliationLog.length - 1
                  ]
                : null;
            const hasFutureConciliations =
              lastConciliation &&
              normalizeDate(new Date(lastConciliation.conciliationDate)) >
                normalizeDate(operationDate);
            return {
              id: doc.id,
              folio: withArrear?.folio ?? doc.id,
              amountToConciliate: doc.amountToConciliate ?? 0,
              totalRemainingNet: withArrear?.totalRemainingNet ?? 0,
              hasFutureConciliations,
            };
          },
        )
      : [];

  const comparisonsWithWarnings = comparisonRows.filter(
    (row) =>
      row.amountToConciliate !== row.totalRemainingNet ||
      row.hasFutureConciliations,
  );
  const hasAmountDifferences = comparisonRows.some(
    (row) => row.amountToConciliate !== row.totalRemainingNet,
  );
  const hasFutureConciliations = comparisonRows.some(
    (row) => row.hasFutureConciliations,
  );
  const hasComparisonWarnings = hasAmountDifferences || hasFutureConciliations;
  const cantContinue =
    hasFutureConciliations && !allowOperationIfFutureConciliations;
  const isActiveOrCompleted =
    selectedRequest.status === 'Activa' ||
    selectedRequest.status === 'Completada';

  const updateRow = (index: number, patch: Partial<PaymentGrouped>) => {
    setPaymentGrouped((prev) =>
      prev.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  };

  function fillPaymentGroupedWithPayoutPlan() {
    if (!payoutPlan) return;
    const filled = payoutPlan.map((payout: PayoutDistribution) => ({
      ...EMPTY_PAYMENT_GROUPED,
      bankAccountDestinationId: payout.bankAccount.id,
      amount: payout.amount,
    }));
    setPaymentGrouped(filled);
  }

  const amounInNominee = paymentGrouped.reduce(
    (acc, row) => acc + row.amount,
    0,
  );

  const handleGenerateNominee = async () => {
    const paymentGenerated = [];
    for (let i = 0; i < paymentGrouped.length; i++) {
      const row = paymentGrouped[i];
      let remainingAmount = Number(row.amount);
      const cutValue = resolveCut(row.cut, row.amount);
      const account = availableAccounts.find(
        (x: any) => x.id === row.bankAccountDestinationId,
      );
      const accountOrigin = originAccounts.find(
        (x: any) => x.id === row.bankAccountOriginId,
      );
      while (remainingAmount > 0) {
        const amountToDeposit =
          remainingAmount > cutValue ? cutValue : remainingAmount;
        paymentGenerated.push({
          businessDestinationName: selectedRequest.businessName,
          bankAccountDestinationId: row.bankAccountDestinationId,
          bankAccountOriginId: row.bankAccountOriginId,
          bankAccountDestinationName: account.bankName,
          bankAccountDestinationNumber: account.accountNumber,
          bankAccountDestinationAccountType: account.accountType,
          bankAccountDestinationHolderName: account.holderName,
          bankAccountDestinationHolderIdentifier: account.holderIdentifier,
          bankAccountOriginName: accountOrigin.bankName,
          bankName: account.bankName,
          accountNumber: account.accountNumber,
          accountType: account.accountType,
          holderName: account.holderName,
          holderIdentifier: account.holderIdentifier,
          businessIdentifier: account.businessIdentifier,
          amount: amountToDeposit,
          operationId: selectedRequest.id,
          executedAt: null,
        });
        remainingAmount -= cutValue;
      }
    }
    setPayments(paymentGenerated);
  };

  const handleActivateOperation = async () => {
    setLoading(true);
    const result = await activateNullFactoringRequest(
      dispatch,
      selectedRequest.id,
      {
        amountInOperation: operationToDepost,
        businessIdentifier: selectedRequest.businessIdentifier,
      },
    );
    if (result) {
      setSuccessOpen('Operación activada correctamente');
      handleExit();
    }
    await setUp();
    setLoading(false);
  };

  useEffect(() => {
    fillPaymentGroupedWithPayoutPlan();
  }, [payoutPlan]);

  const disabled = !availableAccounts.length || !originAccounts.length;

  const isGenerateDisabled =
    formatMoney(amounInNominee) !== formatMoney(operationToDepost) ||
    paymentGrouped.some((x) => !x.cut) ||
    paymentGrouped.some((x) => !x.amount) ||
    paymentGrouped.some((x) => !x.bankAccountDestinationId) ||
    paymentGrouped.some((x) => !x.bankAccountOriginId);

  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [];

    if (
      operationToDepost <= 0 &&
      !loading &&
      selectedRequest.status === 'En giro'
    ) {
      buttons.push({
        label: 'Activar operación',
        onClick: () => void handleActivateOperation(),
        color: 'success',
        variant: 'contained',
        disabled: cantContinue,
      });
    }

    if (operationToDepost > 0) {
      buttons.push({
        label: 'Agregar',
        onClick: () =>
          setPaymentGrouped((prev) => [...prev, { ...EMPTY_PAYMENT_GROUPED }]),
        color: 'primary',
        variant: 'contained',
        disabled: disabled || cantContinue,
      });
      buttons.push({
        label: 'Generar',
        onClick: () => void handleGenerateNominee(),
        color: 'primary',
        variant: 'contained',
        disabled: isGenerateDisabled || cantContinue,
      });
    }

    if (onSinNomina != null) {
      buttons.push({
        label: 'Sin nómina',
        onClick: onSinNomina,
        color: 'success',
        variant: 'contained',
      });
    }

    return buttons;
  }, [
    operationToDepost,
    loading,
    cantContinue,
    disabled,
    isGenerateDisabled,
    onSinNomina,
  ]);

  if (isLoading) return <CenteredCircularProgress />;

  return (
    <div>
      <PageHeader variant="dialogHeader">
        <PageHeader.Main>
          <PageHeader.TitleSection
            title="Generación nómina"
            iconType="payment"
            subtitle={`Monto solicitud: ${formatMoney(operationToDepost)}`}
          />
          <PageHeader.ActionButtons buttons={actionButtons} />
        </PageHeader.Main>
      </PageHeader>
      {documentsAppliedWithArrearCalculation &&
        comparisonRows.length > 0 &&
        !isActiveOrCompleted &&
        hasComparisonWarnings && (
          <ApplicationsInOperationWarnings
            hasAmountDifferences={hasAmountDifferences}
            hasFutureConciliations={hasFutureConciliations}
            allowOperationIfFutureConciliations={
              allowOperationIfFutureConciliations
            }
            comparisonsWithWarnings={comparisonsWithWarnings}
          />
        )}

      <div style={{ display: 'flex', padding: '12px 0px' }}>
        <Typography variant="body1" fontWeight={600}>
          Monto solicitud: {formatMoney(operationToDepost)}
        </Typography>
        <Typography variant="body1" marginLeft={4} fontWeight={600}>
          Monto en nómina: {formatMoney(amounInNominee)}
        </Typography>
      </div>

      {operationToDepost <= 0 && loading && <CenteredCircularProgress />}
      {operationToDepost === 0 && !loading && (
        <Box sx={{ mt: 2 }}>
          <AlertBanner
            severity="info"
            title="Importante"
            message={
              selectedRequest.status === 'En giro'
                ? 'Esta operación puede ser activada sin generar nómina.'
                : 'Esta operación fue activada sin generar nómina.'
            }
          />
        </Box>
      )}
      {operationToDepost < 0 && !loading && (
        <Box sx={{ mt: 2 }}>
          <AlertBanner
            severity="error"
            title="Ten cuidado"
            message="Esta operación puede ser activada generando una cuenta por pagar."
          />
        </Box>
      )}

      {disabled ? (
        <Box sx={{ mt: 2 }}>
          <AlertBanner
            severity="info"
            title="No tienes cuentas registradas para este rut."
            message={
              <>
                Crea una para poder generar una nómina. Puedes hacerlo desde{' '}
                <a
                  href="/network/businesses"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  acá
                </a>
                {canReloadAccounts && (
                  <>
                    . Puedes volver a cargar las cuentas desde{' '}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        void setUp();
                      }}
                    >
                      acá
                    </a>
                    .
                  </>
                )}
              </>
            }
          />
        </Box>
      ) : null}

      {!disabled && paymentGrouped.length === 0 && operationToDepost > 0 && (
        <Box sx={{ mt: 2 }}>
          <AlertBanner
            severity="info"
            title="Genera la nómina"
            message="Usa el botón 'Agregar' para ingresar una transferencia."
          />
        </Box>
      )}

      {paymentGrouped.map((row: PaymentGrouped, index: number) => {
        const originOptions: SelectOption<string>[] = originAccounts.map(
          (x: any) => ({
            value: x.id,
            label: `${x.bankName} (${x.accountNumber})`,
          }),
        );
        const destinationOptions: SelectOption<string>[] =
          availableAccounts.map((x: any) => ({
            value: x.id,
            label: `${x.bankName} (${x.accountNumber})`,
          }));
        const transferCount =
          row.amount && row.cut
            ? Math.ceil(row.amount / resolveCut(row.cut, row.amount))
            : null;

        return (
          <Grid
            key={index}
            container
            spacing={2}
            alignItems="center"
            rowSpacing={4}
            style={{ padding: '5px 0px' }}
          >
            <Grid item xs={2}>
              <MoneyInput
                label="Monto"
                value={row.amount || null}
                onChange={(value) => updateRow(index, { amount: value ?? 0 })}
              />
            </Grid>
            <Grid item xs={3}>
              <SelectInput
                label="Cuenta Origen"
                value={row.bankAccountOriginId || null}
                options={originOptions}
                onChange={(value) =>
                  updateRow(index, { bankAccountOriginId: value ?? '' })
                }
              />
            </Grid>
            <Grid item xs={3}>
              <SelectInput
                label="Cuenta Destino"
                value={row.bankAccountDestinationId || null}
                options={destinationOptions}
                onChange={(value) =>
                  updateRow(index, { bankAccountDestinationId: value ?? '' })
                }
              />
            </Grid>
            <Grid item xs={2}>
              <SelectInput
                label="Corte"
                value={row.cut || null}
                options={AVAILABLE_CUTS}
                onChange={(value) => updateRow(index, { cut: value ?? '' })}
              />
            </Grid>
            <Grid item xs={2}>
              <Grid container alignItems="center">
                <Grid item xs={9}>
                  <Typography textAlign="center" variant="body2">
                    {transferCount != null
                      ? `N° de transferencias: ${transferCount}`
                      : 'Complete los campos'}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <IconButton
                    onClick={() =>
                      setPaymentGrouped((prev) =>
                        prev.filter((_, i) => i !== index),
                      )
                    }
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );
      })}
    </div>
  );
};

export default GenerateNominee;
